import React, { useEffect, useRef, useState } from "react";
import { BarChart3, Bell, Calendar, CirclePlus, House, MessageCircle, Navigation, User } from "lucide-react";
import { PERFIS } from "../constants/perfis";

// Barra de abas inferior FLUTUANTE (dono, 23/09/2026 — referência: 99Food):
// cartão arredondado solto do rodapé, some ao descer a página e volta ao subir,
// aba ativa numa bolinha laranja. MESMOS itens, rotas e selinhos de sempre —
// nada removido nem inventado.

// Esconde ao descer, mostra ao subir. Fica sempre visível no topo da página e
// quando o dedo para de rolar (a pessoa não pode "perder" o menu).
export function useBarraAoRolar() {
  const [visivel, setVisivel] = useState(true);
  const ultimoY = useRef(0);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    ultimoY.current = window.scrollY || 0;
    let parar = null;
    const aoRolar = () => {
      const y = window.scrollY || 0;
      const desceu = y > ultimoY.current + 6;
      const subiu = y < ultimoY.current - 6;
      if (y < 100) setVisivel(true);            // topo: sempre à vista
      else if (desceu) setVisivel(false);
      else if (subiu) setVisivel(true);
      if (desceu || subiu) ultimoY.current = y;
      clearTimeout(parar);
      parar = setTimeout(() => setVisivel(true), 1400);  // parou de rolar → volta
    };
    window.addEventListener("scroll", aoRolar, { passive: true });
    return () => { window.removeEventListener("scroll", aoRolar); clearTimeout(parar); };
  }, []);
  return visivel;
}

export const BottomNav = ({ tela, nav, cart, notifs, perfilAtivo, chatNaoLidas }) => {
  // Badge do CHAT = conversas com mensagem não lida — e SÓ isso. Antes usava a
  // contagem do SINO (notificações de status): aparecia "27" na aba Chat sem
  // nenhuma conversa pendente. As notificações continuam no sino do topo.
  const nConversas = chatNaoLidas?.conversas || 0;
  const visivel = useBarraAoRolar();

  const getItems = () => {
    if (perfilAtivo === PERFIS.FRETEIRO) {
      // Espelha as abas do Freteiro App (deep-link via data.aba) — inalterado.
      return [
        { k: 'painel_freteiro', l: 'Início', Ico: Navigation },
        { k: 'painel_freteiro', l: 'Corridas', Ico: Bell, data: { aba: 'corridas' }, id: 'fr_corridas' },
        { k: 'painel_freteiro', l: 'Ganhos', Ico: BarChart3, data: { aba: 'ganhos' }, id: 'fr_ganhos' },
        { k: 'chat', l: 'Chat', Ico: MessageCircle },
        { k: 'painel_freteiro', l: 'Conta', Ico: User, data: { aba: 'conta' }, id: 'fr_conta' },
      ];
    }
    // Locatário E Locador: mesmas abas. "Locações" abre a CENTRAL ÚNICA.
    return [
      { k: 'home', l: 'Início', Ico: House },
      { k: 'chat', l: 'Chat', Ico: MessageCircle },
      { k: 'publicar', l: 'Anunciar', Ico: CirclePlus, destaque: true },
      { k: 'locacoes_central', l: 'Locações', Ico: Calendar },
      { k: 'perfil', l: 'Perfil', Ico: User },
    ];
  };

  const items = getItems();
  const selo = "absolute top-1 right-1.5 min-w-[17px] h-[17px] px-1 text-white text-[9px] font-bold rounded-full flex items-center justify-center border-2 border-white";

  return (
    <nav
      data-visivel={visivel ? "sim" : "nao"}
      className={`fixed left-0 right-0 z-40 md:hidden transition-all duration-300 ease-out
                  ${visivel ? "translate-y-0 opacity-100" : "translate-y-[140%] opacity-0 pointer-events-none"}`}
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 10px)" }}
    >
      <div className="mx-3 max-w-lg sm:mx-auto flex items-end gap-0.5 px-1.5 pt-1.5 pb-2
                      bg-white/95 backdrop-blur rounded-[30px]
                      shadow-[0_10px_34px_rgba(2,6,23,0.18)] ring-1 ring-black/5">
        {items.map((it, idx) => {
          // Só UMA aba acesa. No perfil freteiro 4 abas usam a mesma rota
          // (painel_freteiro?aba=...) e todas ficavam laranja ao mesmo tempo —
          // com a bolinha nova isso viraria quatro bolas acesas.
          const ativo = idx === items.findIndex(x => x.k === tela);
          const Ico = it.Ico;
          return (
            <button
              key={it.id || it.k}
              type="button"
              onClick={() => nav(it.k, it.data)}
              aria-current={ativo ? "page" : undefined}
              style={{ touchAction: 'manipulation' }}
              className={`flex-1 flex flex-col items-center justify-end h-[52px] rounded-[24px] relative
                          transition-transform duration-150 active:scale-90
                          ${ativo ? 'text-orange-600' : 'text-slate-600 hover:text-orange-600'}`}
            >
              {/* Aba acesa: bolinha GRANDE só com o ícone, sem o nome embaixo —
                  ela sobe um pouco e "estoura" a borda do cartão (igual ao 99).
                  O nome continua no HTML, invisível, pra leitor de tela. */}
              <span
                className={`rounded-full flex items-center justify-center
                            transition-all duration-300 ease-out
                            ${ativo
                              ? 'w-[54px] h-[54px] -mt-4 bg-orange-500 text-white ring-4 ring-white'
                              : 'w-9 h-9 bg-transparent'}`}
              >
                <Ico size={ativo ? 26 : (it.destaque ? 22 : 21)} strokeWidth={ativo ? 2.2 : 1.6} />
              </span>
              <span
                className={`leading-none font-semibold overflow-hidden transition-all duration-300 ease-out
                            ${ativo ? 'sr-only' : 'text-[10.5px] opacity-100 mt-1 max-h-4'}`}
                style={{ letterSpacing: '-0.01em' }}
              >
                {it.l}
              </span>

              {/* Selinho do carrinho na aba Locações */}
              {it.k === 'locacoes_central' && (cart || []).length > 0 && (
                <span className={`${selo} bg-orange-600`}>{cart.length}</span>
              )}
              {/* Selinho de não lidas no Chat — só conversas de chat */}
              {it.k === 'chat' && nConversas > 0 && (
                <span className={`${selo} bg-red-500`}>{nConversas}</span>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
};
